import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useAddDocumentThinker,
  useAvailableThinkers,
  useDocumentAiSettings,
  useDocumentThinker,
  usePinDocumentThinker,
  useRemoveDocumentThinker,
  useUpdateDocumentAiSettings,
} from "~/api/queries";
import { ThinkerPicker } from "~/features/thinkers/ThinkerPicker";
import { Button } from "~/foundation/components/Button";
import { Switch } from "~/foundation/components/Switch";
import { Text } from "~/foundation/components/Text";
import { Tooltip } from "~/foundation/components/Tooltip";

/** The verbatim source labels — a resolution the reader cannot trace is not an answer. */
const SOURCE_LABEL: Record<string, string> = {
  explicit: "for this action",
  collection: "pinned to collection",
  "team-local-default": "team default",
  "system-default": "system default",
  "user-pin": "pinned for you",
};

/**
 * Who answers for this document, outside a conversation (W8 §B.4).
 *
 * Two settings that kms never joined up: the document's assigned thinkers and its
 * `aiAssistanceEnabled` flag. `documents.update_ai_settings` exists on the platform and NO kms
 * surface ever called it, so the switch below is the first thing that has.
 */
export function DocumentAiSettings({
  documentId,
  collectionId,
  canUpdate,
}: {
  documentId: string;
  collectionId: string | null;
  canUpdate: boolean;
}) {
  const { t } = useTranslation();
  const settings = useDocumentAiSettings(documentId);
  const resolution = useDocumentThinker({
    documentId,
    ...(collectionId ? { collectionId } : {}),
  });
  const { data: available } = useAvailableThinkers();
  const update = useUpdateDocumentAiSettings();
  const addThinker = useAddDocumentThinker();
  const removeThinker = useRemoveDocumentThinker();
  const pin = usePinDocumentThinker();
  const [pickerOpen, setPickerOpen] = useState(false);

  const assigned = settings.data?.assignedThinkerIds ?? [];
  const nameOf = (id: string) => (available ?? []).find((entry) => entry.id === id)?.name ?? id;
  const resolved = resolution.data?.resolved ?? null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <Switch
        checked={settings.data?.aiAssistanceEnabled !== false}
        disabled={!canUpdate}
        label={t("AI assistance on this document")}
        description={t("Turn this off and the document offers no AI affordances at all.")}
        onCheckedChange={(checked) =>
          update.mutate({ documentId, patch: { aiAssistanceEnabled: checked } })
        }
      />

      <div className="flex flex-col gap-1 border-t border-divider pt-2">
        <Tooltip content={t("AI for selection actions and transforms, outside of conversations.")}>
          <Text variant="small" className="text-text">
            {t("Document AI")}
          </Text>
        </Tooltip>
        {resolved?.thinkerId ? (
          <Text variant="small" className="text-text-secondary">
            {resolved.displayName}
            {" · "}
            {t(SOURCE_LABEL[resolved.source] ?? "system default")}
            {resolved.fallbackFrom
              ? ` · ${t("(fallback — {{name}} offline)", { name: resolved.fallbackFrom })}`
              : ""}
          </Text>
        ) : (
          <Text variant="small" className="text-text-tertiary">
            {t("No document AI resolved yet.")}
          </Text>
        )}
        <div className="flex flex-wrap gap-1">
          {(resolution.data?.options ?? []).map((option) => (
            <Tooltip
              key={option.thinkerId}
              content={
                option.usable === false
                  ? (option.reason ?? t("Shared for viewing only"))
                  : option.kind === "ide"
                    ? t(
                        "Answers with access to that project's code; may need the owner's approval.",
                      )
                    : t("Use for documents in this collection")
              }
            >
              <Button
                size="sm"
                variant={resolved?.thinkerId === option.thinkerId ? "primary" : "neutral"}
                disabled={!canUpdate || option.usable === false || pin.isPending}
                onClick={() =>
                  pin.mutate(
                    option.kind === "ide"
                      ? {
                          // An IDE pin is ALWAYS yours alone — never collection-wide.
                          thinkerId: option.thinkerId,
                          scope: "user",
                          documentId,
                          ...(option.machineId ? { machineId: option.machineId } : {}),
                          ...(option.workspacePath ? { workspacePath: option.workspacePath } : {}),
                        }
                      : {
                          thinkerId: option.thinkerId,
                          ...(collectionId ? { collectionId } : {}),
                        },
                  )
                }
              >
                {option.displayName}
                {option.usable === false ? ` · ${t("view only")}` : ""}
              </Button>
            </Tooltip>
          ))}
        </div>
        {!collectionId ? (
          <Text variant="small" className="text-text-tertiary">
            {t("This document isn't in a collection yet.")}
          </Text>
        ) : null}
      </div>

      <div className="flex flex-col gap-1 border-t border-divider pt-2">
        <div className="flex items-center gap-2">
          <Text variant="small" className="text-text">
            {t("Thinkers assigned to this document")}
          </Text>
          <Button
            size="sm"
            className="ms-auto"
            disabled={!canUpdate}
            onClick={() => setPickerOpen(true)}
          >
            {t("Add")}
          </Button>
        </div>
        {assigned.length === 0 ? (
          <Text variant="small" className="text-text-tertiary">
            {t("None yet — new conversations start empty.")}
          </Text>
        ) : (
          <ul className="flex flex-col gap-1">
            {assigned.map((id) => (
              <li key={id} className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-body text-text">{nameOf(id)}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={!canUpdate}
                  onClick={() => removeThinker.mutate({ documentId, thinkerId: id })}
                >
                  {t("Remove")}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ThinkerPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        selected={assigned}
        onSelect={(next) => {
          for (const id of next) {
            if (!assigned.includes(id)) {
              addThinker.mutate({ documentId, thinkerId: id });
            }
          }
        }}
        multiple
        filter="ai"
        documentId={documentId}
        title={t("Assign thinkers to this document")}
        confirmLabel={t("Assign")}
      />
    </div>
  );
}
